import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Select, RadioGroup, Alert, Spinner, Breadcrumbs, Stepper } from '@glplus/glpweb-dsl';
import { useVendor } from '../../lib/CapabilitiesContext';
import { VendorApiError, vendorApiClient, type ImportOriginalUpload, type ImportPreview } from '../../lib/vendorApiClient';
import { readSpreadsheet, SpreadsheetError, type ParsedSheet } from '../../lib/spreadsheet';
import {
  IMPORT_TARGETS,
  columnExample,
  mappingProblem,
  notInFile,
  originalsToKeep,
  rowPieces,
  rowTotal,
  setTarget,
  sheetGroups,
  targetOf,
  usablePhotos,
  type ImportBlanks,
  type ImportMapping,
  type ImportTarget,
  type SheetLocation,
} from '../../lib/importFlow';
import { BORDER, GREEN_INK, HEADING, MUTED, RED, FieldLabel, PageTitle, describeProductError } from './parts';

/**
 * P6 + P7 — Upload a spreadsheet, then say what each column holds (Figma
 * `fVTA2OnSEz4UOdTeoXeByH` 137:976, 137:1093).
 *
 * The file is read here, in the browser; what is sent is its rows. We never
 * ask for a particular layout (§15d) — one sheet, a store column, a tab per
 * store or a file per store all work — so the second step is the vendor
 * telling us what their columns mean, with our guess filled in.
 */

type Step = 'upload' | 'match' | 'sending';

interface Sending {
  phase: 'photos' | 'rows' | 'starting';
  done: number;
  total: number;
}

const PHOTO_UPLOADS_AT_ONCE = 3;

export default function ImportSpreadsheet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeVendorId, has } = useVendor();
  const canEdit = has('catalog:edit');

  const [step, setStep] = useState<Step>('upload');
  const [sheets, setSheets] = useState<ParsedSheet[]>([]);
  const [photos, setPhotos] = useState<File[]>([]);
  const [spreadsheets, setSpreadsheets] = useState<File[]>([]);
  const [photosSkipped, setPhotosSkipped] = useState(0);
  const [reading, setReading] = useState(false);
  const [fileProblems, setFileProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [preview, setPreview] = useState<ImportPreview | null>(null);
  /** One answer per group of tabs that share their headers. */
  const [mappings, setMappings] = useState<ImportMapping[]>([]);
  const [locations, setLocations] = useState<SheetLocation[]>([]);
  const [blanks, setBlanks] = useState<ImportBlanks>({ barcode: 'none', photo: 'ask', nutritionLabel: 'ask' });
  const [editStores, setEditStores] = useState(false);
  const [sending, setSending] = useState<Sending | null>(null);

  const groups = useMemo(() => sheetGroups(sheets), [sheets]);
  const total = rowTotal(sheets);
  const files = useMemo(() => [...new Set(sheets.map((s) => s.file))], [sheets]);
  const remembered = preview !== null && preview.sheets.length > 0 && preview.sheets.every((s) => s.remembered);

  if (!activeVendorId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  const addFiles = async (picked: File[]) => {
    setReading(true);
    setError(null);
    setPreview(null);
    const problems: string[] = [];
    let next = sheets;
    let nextFiles = spreadsheets;
    for (const file of picked) {
      try {
        const read = await readSpreadsheet(file);
        // The same file chosen again replaces what was read from it before.
        next = [...next.filter((s) => s.file !== file.name), ...read];
        nextFiles = [...nextFiles.filter((f) => f.name !== file.name), file];
      } catch (e) {
        problems.push(t(`products.import.fileProblem.${e instanceof SpreadsheetError ? e.problem : 'unreadable'}`, { file: file.name }));
      }
    }
    setSheets(next);
    setSpreadsheets(nextFiles);
    setFileProblems(problems);
    setReading(false);
  };

  const addPhotos = (picked: File[]) => {
    const { photos: usable, skipped } = usablePhotos(picked);
    const names = new Set(usable.map((p) => p.name));
    setPhotos((prev) => [...prev.filter((p) => !names.has(p.name)), ...usable]);
    setPhotosSkipped((n) => n + skipped);
  };

  const sheetInputs = () => sheets.map((s) => ({ file: s.file, sheet: s.sheet, headers: s.headers, rowCount: s.rows.length }));

  /** P6 → P7: ask for our guess (or the answers given for these columns last time). */
  const toMatch = async () => {
    setBusy(true);
    setError(null);
    try {
      const p = await vendorApiClient.previewImport(activeVendorId, sheetInputs());
      if (total > p.maxRows) {
        setError(t('products.import.tooManyRows', { max: p.maxRows.toLocaleString(), count: total }));
        return;
      }
      setPreview(p);
      setMappings(groups.map((g) => p.sheets[g[0]].mapping));
      setLocations(p.sheets.map((s) => s.location));
      setBlanks(p.blanks);
      setEditStores(false);
      // Columns we have seen before need no second look — unless they want one.
      if (!p.sheets.every((s) => s.remembered)) setStep('match');
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setBusy(false);
    }
  };

  const groupOf = (sheetIndex: number) => groups.findIndex((g) => g.includes(sheetIndex));

  const runImport = async () => {
    if (!preview) return;
    for (let i = 0; i < sheets.length; i++) {
      const problem = mappingProblem(mappings[groupOf(i)] ?? {}, locations[i]);
      if (problem) {
        setStep('match');
        setError(t(`products.errors.${problem}`));
        return;
      }
    }
    setError(null);
    setStep('sending');
    try {
      // Photos first: the import is told where each one is kept.
      const uploaded: { name: string; url: string }[] = [];
      let done = 0;
      setSending({ phase: 'photos', done: 0, total: photos.length });
      const queue = [...photos];
      const worker = async () => {
        for (let file = queue.shift(); file; file = queue.shift()) {
          const { cdnUrl } = await vendorApiClient.uploadAsset(activeVendorId, 'product-photo', file);
          uploaded.push({ name: file.name, url: cdnUrl });
          done++;
          setSending({ phase: 'photos', done, total: photos.length });
        }
      };
      await Promise.all(Array.from({ length: Math.min(PHOTO_UPLOADS_AT_ONCE, photos.length) }, worker));

      const kept = originalsToKeep(spreadsheets, files);
      const created = await vendorApiClient.createImport(activeVendorId, {
        sheets: sheetInputs().map((s, i) => ({ ...s, mapping: mappings[groupOf(i)], location: locations[i] })),
        blanks,
        photos: uploaded,
        originals: kept.map((f) => ({ name: f.name, bytes: f.size })),
      });
      const { batchId } = created;
      const backup = backUpOriginals(activeVendorId, batchId, created.originals ?? [], kept);
      const pieces = rowPieces(sheets);
      let sent = 0;
      setSending({ phase: 'rows', done: 0, total });
      for (const piece of pieces) {
        await vendorApiClient.addImportRows(activeVendorId, batchId, piece);
        sent += piece.rows.length;
        setSending({ phase: 'rows', done: sent, total });
      }
      setSending({ phase: 'starting', done: total, total });
      await vendorApiClient.startImport(activeVendorId, batchId);
      const backedUp = await backup;
      void navigate(`/products/add/import/${batchId}`, backedUp ? undefined : { state: { backupFailed: true } });
    } catch (e) {
      setSending(null);
      setStep(remembered ? 'upload' : 'match');
      const sheetAt = e instanceof VendorApiError && e.sheetIndex !== undefined ? sheets[e.sheetIndex] : undefined;
      setError([sheetAt ? tabLabel(sheetAt) : null, describeProductError(e, t)].filter(Boolean).join(': '));
    }
  };

  /** A copy of each spreadsheet, kept with the import. A failure here never stops the import. */
  const backUpOriginals = async (vendorId: string, batchId: string, uploads: ImportOriginalUpload[], kept: File[]): Promise<boolean> => {
    const results = await Promise.all(
      uploads.map(async (u) => {
        const file = kept.find((f) => f.name === u.name);
        if (!file) return false;
        try {
          const put = await fetch(u.uploadUrl, { method: 'PUT', headers: { 'Content-Type': u.contentType }, body: file });
          if (!put.ok) return false;
          await vendorApiClient.confirmImportOriginal(vendorId, batchId, u.fileId);
          return true;
        } catch {
          return false;
        }
      }),
    );
    return results.every(Boolean);
  };

  const tabLabel = (s: ParsedSheet) => (s.sheet ? t('products.questions.place.tab', { tab: s.sheet }) : s.file);

  const crumbs = (
    <Breadcrumbs
      ariaLabel={t('products.detail.breadcrumbAria')}
      linkComponent={Link}
      items={[
        { label: t('nav.products'), to: '/products' },
        { label: t('products.add.title'), to: '/products/add' },
        { label: t('products.import.crumb'), ariaCurrent: true },
      ]}
    />
  );

  if (!canEdit) {
    return (
      <Box>
        {crumbs}
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('products.add.form.noAccess')}
        </Alert>
      </Box>
    );
  }

  const stepIndex = step === 'upload' ? 0 : step === 'match' ? 1 : 2;
  const steps = (
    <Stepper
      ariaLabel={t('products.import.steps.aria')}
      color="secondary"
      activeStep={stepIndex}
      allowFutureNavigation={false}
      steps={[
        { key: 'upload', label: t('products.import.steps.upload'), disabled: step === 'sending' },
        { key: 'match', label: t('products.import.steps.match'), disabled: step !== 'match' },
        { key: 'import', label: t('products.import.steps.import'), disabled: true },
      ]}
      onStepChange={(_i, key) => {
        if (key === 'upload' && step === 'match') setStep('upload');
      }}
      sx={{ mt: 3, maxWidth: 720 }}
    />
  );

  // ── Sending ────────────────────────────────────────────────────
  if (step === 'sending') {
    return (
      <Box>
        {crumbs}
        <Box sx={{ mt: 2 }}>
          <PageTitle title={t('products.import.sending.title')} subtitle={t('products.import.sending.subtitle')} />
        </Box>
        {steps}
        <Box sx={{ mt: 3, p: 4, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 2 }} role="status" data-testid="import-sending">
          <Spinner color="brand" />
          <Typography sx={{ fontSize: 16, color: HEADING }}>
            {sending ? t(`products.import.sending.${sending.phase}`, { done: sending.done.toLocaleString(), total: sending.total.toLocaleString() }) : ''}
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── P7: match columns ──────────────────────────────────────────
  if (step === 'match' && preview) {
    const storeName = (id: string) => preview.locations.find((l) => l.storeId === id)?.name ?? '';
    const fromColumn = (i: number) => {
      const at = mappings[groupOf(i)]?.location;
      return t('products.import.stores.fromColumn', { column: at === undefined ? '' : columnName(sheets[i].headers, at, t) });
    };
    const locationLabel = (i: number) => {
      const loc = locations[i];
      if (loc.kind === 'store') return storeName(loc.storeId);
      return loc.kind === 'column' ? fromColumn(i) : t('products.import.stores.all', { count: preview.locations.length });
    };

    return (
      <Box>
        {crumbs}
        <Box sx={{ mt: 2 }}>
          <PageTitle
            title={t('products.import.match.title')}
            subtitle={t('products.import.match.subtitle', { file: files.length === 1 ? files[0] : t('products.import.fileCount', { count: files.length }), rows: total.toLocaleString(), count: sheets.length })}
          />
        </Box>
        {steps}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="import-error">
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 300px' }, gap: 3, alignItems: 'start' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            {groups.map((group, g) => {
              const groupSheets = group.map((i) => sheets[i]);
              const headers = groupSheets[0].headers;
              const mapping = mappings[g] ?? {};
              const matched = headers.filter((_h, col) => targetOf(mapping, col) !== null).length;
              const missing = notInFile(mapping);
              return (
                <Card key={group.join('-')} testId={`import-group-${g}`}>
                  <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, color: HEADING }}>
                    {t('products.import.match.matched', { matched, count: headers.length })}
                  </Typography>
                  {groups.length > 1 && (
                    <Typography sx={{ mt: 0.5, fontSize: 14, color: MUTED }}>{groupSheets.map(tabLabel).join(' · ')}</Typography>
                  )}
                  <Typography sx={{ mt: 1, fontSize: 15, fontWeight: 700, color: HEADING }}>{t('products.import.match.check')}</Typography>

                  <Box role="table" aria-label={t('products.import.match.tableAria')} sx={{ mt: 2 }}>
                    <Box role="row" sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: COLUMN_GRID, gap: 2, pb: 1, borderBottom: `1px solid ${BORDER}` }}>
                      {(['yours', 'example', 'holds'] as const).map((k) => (
                        <Box role="columnheader" key={k}>
                          <FieldLabel>{t(`products.import.match.col.${k}`)}</FieldLabel>
                        </Box>
                      ))}
                    </Box>
                    {headers.map((_header, col) => {
                      const ex = columnExample(groupSheets, col);
                      const name = columnName(headers, col, t);
                      const current = targetOf(mapping, col);
                      return (
                        <Box role="row" key={col} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: COLUMN_GRID }, gap: { xs: 0.5, md: 2 }, alignItems: 'center', py: 1, borderBottom: `1px solid ${BORDER}` }}>
                          <Typography role="cell" sx={{ fontSize: 15, fontWeight: 700, color: HEADING, overflowWrap: 'anywhere' }}>
                            {name}
                          </Typography>
                          <Typography role="cell" sx={{ fontSize: 15, color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ex.example === null
                              ? t('products.import.match.blankAll')
                              : ex.blank > 0
                                ? `${ex.example} · ${t('products.import.match.blankSome', { count: ex.blank })}`
                                : ex.example}
                          </Typography>
                          <Box role="cell" data-testid={`import-column-${g}-${col}`}>
                            <Select
                              ariaLabel={t('products.import.match.holdsAria', { column: name })}
                              value={current ?? SKIP}
                              onChange={(_e, v) => {
                                setError(null);
                                setMappings((prev) => prev.map((m, i) => (i === g ? setTarget(m, col, v === SKIP ? null : (v as ImportTarget)) : m)));
                              }}
                              options={[
                                { value: SKIP, label: t('products.import.match.skip') },
                                ...IMPORT_TARGETS.map((target) => ({ value: target, label: t(`products.import.target.${target}`) })),
                              ]}
                              size="small"
                              fullWidth
                              sx={current ? undefined : { '& .MuiSelect-select': { color: MUTED } }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {missing.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#EAF2FB', borderRadius: '8px' }} data-testid={`import-not-in-file-${g}`}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>{t('products.import.notInFile.title', { count: missing.length })}</Typography>
                      <Typography sx={{ mt: 0.5, fontSize: 15, color: '#343434' }}>
                        {t('products.import.notInFile.body', { things: missing.map((m) => t(`products.import.notInFile.thing.${m}`)).join(', ') })}
                      </Typography>
                    </Box>
                  )}
                </Card>
              );
            })}

            <Card testId="import-blanks">
              <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, color: HEADING }}>
                {t('products.import.blanks.title')}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 15, color: MUTED }}>{t('products.import.blanks.intro')}</Typography>
              {mappings.some((m) => m.barcode !== undefined) ? (
                <BlankQuestion
                  which="barcode"
                  value={blanks.barcode}
                  options={['none', 'reject']}
                  onChange={(v) => setBlanks((b) => ({ ...b, barcode: v as ImportBlanks['barcode'] }))}
                />
              ) : (
                <Typography sx={{ mt: 2, fontSize: 15, color: HEADING }}>{t('products.import.blanks.noBarcodeColumn')}</Typography>
              )}
              <BlankQuestion which="photo" value={blanks.photo} options={['ask', 'none']} onChange={(v) => setBlanks((b) => ({ ...b, photo: v as ImportBlanks['photo'] }))} />
              <BlankQuestion
                which="nutritionLabel"
                value={blanks.nutritionLabel}
                options={['ask', 'none']}
                onChange={(v) => setBlanks((b) => ({ ...b, nutritionLabel: v as ImportBlanks['nutritionLabel'] }))}
              />
            </Card>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
              <Button ariaLabel={t('products.import.back')} buttonText={t('products.import.back')} variant="text" onClick={() => setStep('upload')} dataTestId="import-back" />
              <Button
                ariaLabel={t('products.import.importRows', { count: total, rows: total.toLocaleString() })}
                buttonText={t('products.import.importRows', { count: total, rows: total.toLocaleString() })}
                variant="contained"
                color="primary"
                onClick={() => void runImport()}
                dataTestId="import-run"
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Card testId="import-stores">
              <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, color: HEADING }}>
                {t('products.import.stores.title')}
              </Typography>
              {preview.locations.length === 1 ? (
                <Typography sx={{ mt: 1, fontSize: 14, color: MUTED }}>{t('products.import.stores.only', { store: preview.locations[0].name })}</Typography>
              ) : (
                <>
                  <Typography sx={{ mt: 1, fontSize: 14, color: MUTED }}>{t('products.import.stores.intro')}</Typography>
                  <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: editStores ? 1.5 : 0.5 }}>
                    {sheets.map((s, i) => {
                      const loc = locations[i];
                      const hasColumn = mappings[groupOf(i)]?.location !== undefined;
                      const value = loc.kind === 'store' ? loc.storeId : loc.kind;
                      return editStores ? (
                        <Box key={`${s.file}|${s.sheet}`} data-testid={`import-store-${i}`}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: HEADING, mb: 0.5 }}>{tabLabel(s)}</Typography>
                          <Select
                            ariaLabel={t('products.import.stores.pickAria', { tab: tabLabel(s) })}
                            value={value}
                            onChange={(_e, v) =>
                              setLocations((prev) => prev.map((l, j) => (j !== i ? l : v === 'all' || v === 'column' ? { kind: v } : { kind: 'store', storeId: v })))
                            }
                            options={[
                              { value: 'all', label: t('products.import.stores.all', { count: preview.locations.length }) },
                              ...(hasColumn ? [{ value: 'column', label: fromColumn(i) }] : []),
                              ...preview.locations.map((l) => ({ value: l.storeId, label: l.name })),
                            ]}
                            size="small"
                            fullWidth
                          />
                        </Box>
                      ) : (
                        <Typography key={`${s.file}|${s.sheet}`} sx={{ fontSize: 14, color: '#343434' }} data-testid={`import-store-${i}`}>
                          {tabLabel(s)} → {locationLabel(i)}
                        </Typography>
                      );
                    })}
                  </Box>
                  {!editStores && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        ariaLabel={t('products.import.stores.change')}
                        buttonText={t('products.import.stores.change')}
                        variant="outlinedGreen"
                        onClick={() => setEditStores(true)}
                        dataTestId="import-stores-change"
                      />
                    </Box>
                  )}
                </>
              )}
            </Card>
            <Card>
              <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, color: HEADING }}>
                {t('products.import.remember.title')}
              </Typography>
              <Typography sx={{ mt: 1, fontSize: 14, color: MUTED }}>{t('products.import.remember.body')}</Typography>
            </Card>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── P6: upload ─────────────────────────────────────────────────
  return (
    <Box>
      {crumbs}
      <Box sx={{ mt: 2 }}>
        <PageTitle title={t('products.import.upload.title')} subtitle={t('products.import.upload.subtitle')} />
      </Box>
      {steps}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="import-error">
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 300px' }, gap: 3, alignItems: 'start' }}>
        <Card>
          <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, color: HEADING }}>
            {t('products.import.upload.file.title')}
          </Typography>
          <DropZone
            title={t('products.import.upload.file.drop')}
            hint={t('products.import.upload.file.hint')}
            accept=".xlsx,.csv,.tsv,.txt"
            disabled={reading || busy}
            busy={reading}
            onFiles={(f) => void addFiles(f)}
            testId="import-files"
          />
          {fileProblems.map((p) => (
            <Typography key={p} role="alert" sx={{ mt: 1, fontSize: 14, fontWeight: 700, color: RED }} data-testid="import-file-problem">
              {p}
            </Typography>
          ))}

          <Typography sx={{ mt: 2.5, fontSize: 15, fontWeight: 700, color: HEADING }}>{t('products.import.upload.shapes.title')}</Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(['one', 'column', 'tabs', 'files'] as const).map((k) => (
              <Typography component="li" key={k} sx={{ fontSize: 15, color: '#343434', '&::marker': { color: '#3DA026' } }}>
                {t(`products.import.upload.shapes.${k}`)}
              </Typography>
            ))}
          </Box>

          {files.map((file) => {
            const mine = sheets.filter((s) => s.file === file);
            const rows = rowTotal(mine);
            const tabs = mine.filter((s) => s.sheet).length;
            return (
              <Box key={file} sx={{ mt: 2, p: 1.5, border: `1px solid ${BORDER}`, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 2 }} data-testid="import-file">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING, overflowWrap: 'anywhere' }}>{file}</Typography>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>
                    {[tabs > 1 ? t('products.import.upload.tabs', { count: tabs }) : null, t('products.import.upload.rows', { count: rows, rows: rows.toLocaleString() })].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Button
                  ariaLabel={t('products.import.upload.removeAria', { file })}
                  buttonText={t('products.import.upload.remove')}
                  variant="text"
                  textColor={RED}
                  disabled={busy}
                  onClick={() => {
                    setSheets((prev) => prev.filter((s) => s.file !== file));
                    setSpreadsheets((prev) => prev.filter((f) => f.name !== file));
                    setPreview(null);
                  }}
                />
              </Box>
            );
          })}

          <Typography component="h2" sx={{ mt: 3, fontSize: 21, fontWeight: 600, color: HEADING }}>
            {t('products.import.upload.photos.title')}
          </Typography>
          <DropZone
            title={t('products.import.upload.photos.drop')}
            hint={t('products.import.upload.photos.hint')}
            accept="image/jpeg,image/png,image/webp"
            disabled={busy}
            busy={false}
            onFiles={addPhotos}
            testId="import-photos"
          />
          {(photos.length > 0 || photosSkipped > 0) && (
            <Box sx={{ mt: 2, p: 1.5, border: `1px solid ${BORDER}`, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 2 }} data-testid="import-photo-count">
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>{t('products.import.upload.photos.count', { count: photos.length })}</Typography>
                <Typography sx={{ fontSize: 13, color: photosSkipped > 0 ? RED : MUTED }}>
                  {photosSkipped > 0 ? t('products.import.upload.photos.skipped', { count: photosSkipped }) : t('products.import.upload.photos.kinds')}
                </Typography>
              </Box>
              <Button
                ariaLabel={t('products.import.upload.photos.removeAria')}
                buttonText={t('products.import.upload.remove')}
                variant="text"
                textColor={RED}
                disabled={busy}
                onClick={() => {
                  setPhotos([]);
                  setPhotosSkipped(0);
                }}
              />
            </Box>
          )}

          {remembered ? (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#EAF5EA', borderRadius: '8px' }} data-testid="import-remembered">
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>{t('products.import.remembered.title')}</Typography>
              <Typography sx={{ mt: 0.5, fontSize: 15, color: '#343434' }}>{t('products.import.remembered.body')}</Typography>
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  ariaLabel={t('products.import.remembered.check')}
                  buttonText={t('products.import.remembered.check')}
                  variant="text"
                  onClick={() => setStep('match')}
                  dataTestId="import-check-columns"
                />
                <Button
                  ariaLabel={t('products.import.importRows', { count: total, rows: total.toLocaleString() })}
                  buttonText={t('products.import.importRows', { count: total, rows: total.toLocaleString() })}
                  variant="contained"
                  color="primary"
                  onClick={() => void runImport()}
                  dataTestId="import-run"
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                ariaLabel={t('products.import.continue')}
                buttonText={t('products.import.continue')}
                variant="contained"
                color="primary"
                disabled={sheets.length === 0 || reading || busy}
                onClick={() => void toMatch()}
                dataTestId="import-continue"
              />
            </Box>
          )}
        </Card>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card>
            <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, color: HEADING }}>
              {t('products.import.again.title')}
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 14, color: MUTED }}>{t('products.import.again.body')}</Typography>
          </Card>
          <Card>
            <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, color: HEADING }}>
              {t('products.import.needs.title')}
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(['name', 'price', 'barcode', 'description', 'photo'] as const).map((k) => (
                <Typography component="li" key={k} sx={{ fontSize: 14, color: '#343434' }}>
                  {t(`products.import.needs.${k}`)}
                </Typography>
              ))}
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

/** The picker's value for a column that is left out of the import. */
const SKIP = 'skip';
const COLUMN_GRID = 'minmax(120px, 1fr) minmax(0, 1.5fr) minmax(200px, 1.6fr)';

/** The vendor's own header, or "Column C" for a column they left unnamed. */
function columnName(headers: string[], column: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return headers[column] || t('products.import.match.unnamed', { letter: columnLetter(column) });
}

function columnLetter(index: number): string {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

function Card({ children, testId }: { children: React.ReactNode; testId?: string }) {
  return (
    <Box data-testid={testId} sx={{ p: 3, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px', minWidth: 0 }}>
      {children}
    </Box>
  );
}

function BlankQuestion({ which, value, options, onChange }: { which: 'barcode' | 'photo' | 'nutritionLabel'; value: string; options: string[]; onChange: (value: string) => void }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ mt: 2 }} data-testid={`import-blank-${which}`}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>{t(`products.import.blanks.${which}.label`)}</Typography>
      <RadioGroup
        ariaLabel={t(`products.import.blanks.${which}.label`)}
        name={`import-blank-${which}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        options={options.map((o) => ({ value: o, label: t(`products.import.blanks.${which}.${o}`) }))}
      />
    </Box>
  );
}

/** A place to drop files, with a button for those who would rather browse. */
function DropZone({
  title,
  hint,
  accept,
  disabled,
  busy,
  onFiles,
  testId,
}: {
  title: string;
  hint: string;
  accept: string;
  disabled: boolean;
  busy: boolean;
  onFiles: (files: File[]) => void;
  testId: string;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <Box
      data-testid={testId}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled && e.dataTransfer.files.length > 0) onFiles([...e.dataTransfer.files]);
      }}
      sx={{
        mt: 2,
        p: 4,
        border: `2px dashed ${over ? GREEN_INK : '#9AA3AD'}`,
        borderRadius: '8px',
        bgcolor: over ? '#EAF5EA' : '#F7F7F8',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        textAlign: 'center',
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: HEADING }}>{title}</Typography>
      <Typography sx={{ fontSize: 14, color: '#343434' }}>{hint}</Typography>
      <input
        ref={input}
        type="file"
        accept={accept}
        multiple
        hidden
        data-testid={`${testId}-input`}
        onChange={(e) => {
          const picked = [...(e.target.files ?? [])];
          if (picked.length > 0) onFiles(picked);
          e.target.value = '';
        }}
      />
      {busy ? (
        <Spinner color="brand" />
      ) : (
        <Button ariaLabel={`${t('products.import.upload.browse')} — ${title}`} buttonText={t('products.import.upload.browse')} variant="outlinedGreen" disabled={disabled} onClick={() => input.current?.click()} />
      )}
    </Box>
  );
}
